// AG Grid 31 equivalent of Raj's durable-state allowlist.
function record(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function columnIds(columns) {
  return (columns || []).flatMap(function (column) {
    return column.children ? columnIds(column.children) : [column.colId || column.field].filter(Boolean);
  });
}

// Stateful colDef values are defaults, not commands to overwrite a user's
// current layout whenever a parent renders (for example while editing a cell).
export function initialColumnDefaults(column = {}) {
  const result = { ...column };
  for (const name of ["width", "flex", "hide", "sort", "sortIndex", "pinned", "aggFunc", "rowGroup", "rowGroupIndex", "pivot", "pivotIndex"]) {
    if (name in result) {
      result["initial" + name[0].toUpperCase() + name.slice(1)] = result[name];
      delete result[name];
    }
  }
  if (result.children) result.children = result.children.map(initialColumnDefaults);
  return result;
}

export function sanitizeGridState(value, allowedIds) {
  if (!record(value)) return undefined;
  const ids = new Set(allowedIds);
  function strings(list) {
    return Array.isArray(list) ? [...new Set(list.filter(function (id) { return typeof id === "string" && ids.has(id); }))] : [];
  }
  function entries(list) {
    return Array.isArray(list) ? list.filter(function (entry) { return record(entry) && ids.has(entry.colId); }) : [];
  }
  const state = {};
  if (record(value.columnOrder)) state.columnOrder = { orderedColIds: strings(value.columnOrder.orderedColIds) };
  if (record(value.columnPinning)) state.columnPinning = {
    leftColIds: strings(value.columnPinning.leftColIds), rightColIds: strings(value.columnPinning.rightColIds),
  };
  if (record(value.columnVisibility)) state.columnVisibility = { hiddenColIds: strings(value.columnVisibility.hiddenColIds) };
  if (record(value.rowGroup)) state.rowGroup = { groupColIds: strings(value.rowGroup.groupColIds) };
  if (record(value.pivot)) state.pivot = { pivotMode: value.pivot.pivotMode === true, pivotColIds: strings(value.pivot.pivotColIds) };
  if (record(value.columnSizing)) state.columnSizing = {
    columnSizingModel: entries(value.columnSizing.columnSizingModel).flatMap(function (entry) {
      const width = Number.isFinite(entry.width) && entry.width > 0 ? entry.width : undefined;
      const flex = Number.isFinite(entry.flex) && entry.flex >= 0 ? entry.flex : undefined;
      return width !== undefined || flex !== undefined ? [{ colId: entry.colId, ...(width !== undefined ? { width } : {}), ...(flex !== undefined ? { flex } : {}) }] : [];
    }),
  };
  if (record(value.sort)) state.sort = {
    sortModel: entries(value.sort.sortModel).filter(function (entry) { return entry.sort === "asc" || entry.sort === "desc"; })
      .map(function (entry) { return { colId: entry.colId, sort: entry.sort }; }),
  };
  if (record(value.aggregation)) state.aggregation = {
    aggregationModel: entries(value.aggregation.aggregationModel).filter(function (entry) { return typeof entry.aggFunc === "string"; })
      .map(function (entry) { return { colId: entry.colId, aggFunc: entry.aggFunc }; }),
  };
  if (record(value.filter) && record(value.filter.filterModel)) state.filter = {
    filterModel: Object.fromEntries(Object.entries(value.filter.filterModel).filter(function ([id, model]) { return ids.has(id) && record(model); })),
  };
  if (record(value.sideBar) && typeof value.sideBar.visible === "boolean"
      && ["left", "right"].includes(value.sideBar.position)) {
    state.sideBar = {
      visible: value.sideBar.visible, position: value.sideBar.position,
      openToolPanel: ["columns", "filters"].includes(value.sideBar.openToolPanel) ? value.sideBar.openToolPanel : null,
      toolPanels: {},
    };
    for (const panel of ["columns", "filters"]) {
      const saved = value.sideBar.toolPanels?.[panel];
      if (record(saved)) state.sideBar.toolPanels[panel] = {
        expandedGroupIds: Array.isArray(saved.expandedGroupIds) ? saved.expandedGroupIds.filter(function (id) { return typeof id === "string"; }) : [],
        ...(panel === "filters" ? { expandedColIds: strings(saved.expandedColIds) } : {}),
      };
    }
  }
  return state;
}
